interface SliderInputValueConstraints {
  min: number;
  max: number;
  step: number;
}

const DEFAULT_CONSTRAINTS: SliderInputValueConstraints = {
  min: 0,
  max: 100,
  step: 1,
};

const MAX_TICK_MARKS = 1000;

const decimalPlaces = (value: number): number => {
  const [coefficient, exponentValue = "0"] = value.toString().toLowerCase().split("e");
  const fractionLength = coefficient.split(".")[1]?.length ?? 0;

  return Math.max(0, fractionLength - Number(exponentValue));
};

const shiftDecimal = (value: number, places: number): number => {
  const [coefficient, exponentValue = "0"] = value.toString().toLowerCase().split("e");
  return Number(`${coefficient}e${Number(exponentValue) + places}`);
};

const stabilizeSteppedValue = (value: number, constraints: SliderInputValueConstraints): number => {
  if (Number.isInteger(value)) {
    return value;
  }

  const precision = Math.max(decimalPlaces(constraints.min), decimalPlaces(constraints.step));
  const shiftedValue = shiftDecimal(value, precision);

  if (!Number.isFinite(shiftedValue)) {
    return value;
  }

  const roundedValue = shiftDecimal(Math.round(shiftedValue), -precision);
  return Number.isFinite(roundedValue) ? roundedValue : value;
};

export const normalizeSliderInputConstraints = ({
  min,
  max,
  step,
}: SliderInputValueConstraints): SliderInputValueConstraints => {
  const normalizedMin = Number.isFinite(min) ? min : DEFAULT_CONSTRAINTS.min;
  const finiteMax = Number.isFinite(max) ? max : DEFAULT_CONSTRAINTS.max;

  return {
    min: normalizedMin,
    max: Math.max(normalizedMin, finiteMax),
    step: Number.isFinite(step) && step > 0 ? step : DEFAULT_CONSTRAINTS.step,
  };
};

export const normalizeSliderInputValue = (value: number, constraints: SliderInputValueConstraints): number => {
  const { min, max, step } = normalizeSliderInputConstraints(constraints);
  const clampedValue = Math.min(Math.max(Number.isFinite(value) ? value : min, min), max);
  const stepCount = (max - min) / step;
  const valueStepIndex = Math.round((clampedValue - min) / step);
  const stepCountTolerance = Number.EPSILON * Math.max(1, Math.abs(stepCount)) * 4;
  const maxStepIndex = Number.isFinite(stepCount)
    ? Math.max(0, Math.floor(stepCount + stepCountTolerance))
    : valueStepIndex;
  const steppedValue = min + Math.min(Math.max(valueStepIndex, 0), maxStepIndex) * step;
  const finiteValue = Number.isFinite(steppedValue) ? steppedValue : clampedValue;
  const roundedValue = stabilizeSteppedValue(finiteValue, { min, max, step });

  return Math.min(Math.max(roundedValue, min), max);
};

export const getSliderInputTickPositions = (constraints: SliderInputValueConstraints): number[] => {
  const { min, max, step } = normalizeSliderInputConstraints(constraints);
  const scale = Math.max(Math.abs(min), Math.abs(max), Math.abs(step), 1);
  const scaledRange = max / scale - min / scale;
  const scaledStep = step / scale;

  if (scaledRange === 0) {
    return [0];
  }

  const stepCount = scaledRange / scaledStep;
  const stepCountTolerance = Number.EPSILON * Math.max(1, Math.abs(stepCount)) * 4;
  const maxStepIndex = Math.floor(stepCount + stepCountTolerance);

  if (!Number.isFinite(stepCount) || maxStepIndex + 1 > MAX_TICK_MARKS) {
    return [];
  }

  return Array.from({ length: maxStepIndex + 1 }, (_, index) => Number(((index / stepCount) * 100).toFixed(12)));
};
